import { JSONPath } from 'jsonpath-plus';

import { CallActionPayload, ContextStore, ContextValue } from '../../types';

export const resolvePayload = (
  payload: CallActionPayload,
  context: ContextStore
): Record<string, unknown> => {
  const processTemplateString = (str: string): ContextValue => {
    if (str.startsWith('$context:')) {
      const contextPath = str.slice(9);
      // Convert simple dot notation to JSONPath format
      const jsonPathQuery = contextPath.startsWith('$') ? contextPath : `$.${contextPath}`;

      try {
        const results: ContextValue = JSONPath({ path: jsonPathQuery, json: context });

        if (Array.isArray(results) && results.length === 0) {
          throw new Error(`Context value not found for path: ${contextPath}`);
        }

        // @NOTE-VZ: why only first item?
        return Array.isArray(results) ? results[0] : results;
      } catch (error) {
        throw new Error(`Context value not found for path: ${contextPath}`);
      }
    }

    if (str.includes('{{') && str.includes('}}')) {
      // Parse the template with proper handling of nested braces
      const processItemProperty = (propMatch: string, item: unknown): string => {
        const trimmedPropMatch = propMatch.trim();

        // Check for truncation: path | trunc:50
        const truncMatch = trimmedPropMatch.match(/^(.+?)\s*\|\s*trunc:(\d+)$/);
        const propPath = truncMatch ? truncMatch[1].trim() : trimmedPropMatch;
        const maxLength = truncMatch ? parseInt(truncMatch[2], 10) : undefined;

        const itemJsonPath = propPath.startsWith('$') ? propPath : `$.${propPath}`;

        try {
          const propResults: ContextValue = JSONPath({
            path: itemJsonPath,
            json: item as object,
          });
          const value = Array.isArray(propResults) ? propResults[0] : propResults;

          let stringValue = '';
          if (['string', 'number', 'boolean'].includes(typeof value)) {
            stringValue = String(value);
          } else {
            stringValue = JSON.stringify(value);
          }

          // Apply truncation if specified
          if (maxLength && stringValue.length > maxLength) {
            return `${stringValue.slice(0, maxLength)}...`;
          }

          return stringValue;
        } catch {
          return '';
        }
      };

      const processNestedTemplate = (template: string, item: unknown): string => {
        let result = template;
        let pos = 0;

        while (pos < result.length) {
          const start = result.indexOf('{{', pos);
          if (start === -1) {
            break;
          }

          // Find the matching closing braces
          let braceCount = 0;
          let end = start + 2;

          while (end < result.length) {
            if (result.slice(end, end + 2) === '{{') {
              braceCount += 1;
              end += 2;
            } else if (result.slice(end, end + 2) === '}}') {
              if (braceCount === 0) {
                end += 2;
                break;
              }
              braceCount -= 1;
              end += 2;
            } else {
              end += 1;
            }
          }

          if (end > result.length) {
            // Malformed template, skip
            pos = start + 2;
            break;
          }

          const innerContent = result.slice(start + 2, end - 2);
          const replacement = processItemProperty(innerContent, item);

          result = result.slice(0, start) + replacement + result.slice(end);
          pos = start + replacement.length;
        }

        return result;
      };

      const processTemplateMatch = (match: string): string => {
        const trimmedMatch = match.trim();

        // Check for iterator operator: path | @each: "template"
        const iteratorMatch = trimmedMatch.match(/^(.+?)\s*\|\s*@each:\s*"([\s\S]+)"$/);
        if (iteratorMatch) {
          const [, pathPart, template] = iteratorMatch;
          const jsonPathQuery = pathPart.trim().startsWith('$')
            ? pathPart.trim()
            : `$.${pathPart.trim()}`;

          try {
            const results: ContextValue = JSONPath({ path: jsonPathQuery, json: context });

            if (!Array.isArray(results)) {
              throw new Error(`Iterator operator requires an array result, got: ${typeof results}`);
            }

            // Handle nested array structure - JSONPath sometimes returns [[items]] instead of [items]
            let actualArray = results;
            if (results.length === 1 && Array.isArray(results[0])) {
              const [firstResult] = results;
              actualArray = firstResult;
            }

            if (!Array.isArray(actualArray)) {
              throw new Error(
                `Iterator operator requires an array result, got: ${typeof actualArray}`
              );
            }

            // Process each item with the template
            return actualArray
              .map((item) => {
                // Replace placeholders in template with item properties using proper brace matching
                const processed = processNestedTemplate(template, item);
                // Convert \n escape sequences to actual newlines
                return processed.replace(/\\n/g, '\n');
              })
              .join('');
          } catch (error) {
            throw new Error(
              `Iterator operation failed for "${jsonPathQuery}": ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            );
          }
        }

        // Convert simple dot notation to JSONPath format if needed
        const jsonPathQuery = trimmedMatch.startsWith('$') ? trimmedMatch : `$.${trimmedMatch}`;

        try {
          const results: ContextValue = JSONPath({ path: jsonPathQuery, json: context });

          if (Array.isArray(results)) {
            if (results.length === 0) {
              return '';
            }
            if (
              results.every((result) => ['string', 'number', 'boolean'].includes(typeof result))
            ) {
              return results.join(', ');
            }
            return JSON.stringify(results);
          }

          if (['string', 'number', 'boolean'].includes(typeof results)) {
            return String(results);
          }

          return JSON.stringify(results);
        } catch (error) {
          throw new Error(
            `JSONPath query failed for "${jsonPathQuery}": ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          );
        }
      };

      // Parse templates using proper brace matching
      let result = str;
      let pos = 0;

      while (pos < result.length) {
        const start = result.indexOf('{{', pos);
        if (start === -1) {
          break;
        }

        // Find the matching closing braces
        let braceCount = 0;
        let end = start + 2;

        while (end < result.length) {
          if (result.slice(end, end + 2) === '{{') {
            braceCount += 1;
            end += 2;
          } else if (result.slice(end, end + 2) === '}}') {
            if (braceCount === 0) {
              end += 2;
              break;
            }
            braceCount -= 1;
            end += 2;
          } else {
            end += 1;
          }
        }

        if (end > result.length) {
          // Malformed template, skip
          pos = start + 2;
          break;
        }

        const innerContent = result.slice(start + 2, end - 2);
        const replacement = processTemplateMatch(innerContent);

        result = result.slice(0, start) + replacement + result.slice(end);
        pos = start + replacement.length;
      }

      return result;
    }

    return str;
  };

  // Recursive function to process any value
  const processValue = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return processTemplateString(value);
    }

    if (value !== null && typeof value === 'object') {
      // Process objects recursively
      if (Array.isArray(value)) {
        return value.map(processValue);
      }

      return Object.entries(value).reduce(
        (acc, [k, v]) => ({
          ...acc,
          [k]: processValue(v),
        }),
        {}
      );
    }

    return value;
  };

  if (Array.isArray(payload)) {
    return payload.reduce((acc, key) => {
      // Convert simple dot notation to JSONPath format
      const jsonPathQuery = key.startsWith('$') ? key : `$.${key}`;

      try {
        const results: ContextValue = JSONPath({ path: jsonPathQuery, json: context });
        const value = Array.isArray(results) && results.length > 0 ? results[0] : '';
        return {
          ...acc,
          [key]: String(value),
        };
      } catch {
        return {
          ...acc,
          [key]: '',
        };
      }
    }, {} as Record<string, ContextValue>);
  }

  // Process each key-value pair in the payload
  return Object.entries(payload).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: processValue(value),
    }),
    {} as Record<string, unknown>
  );
};
